/*
| Table behaviour, ported from the prototype: click-to-sort and CSV export.
|
| Both read the rendered cells rather than the source data, which is what makes
| them work for every table on every screen without a per-screen adapter — and
| means the CSV contains exactly what the reader saw, em dashes included.
*/

import { EMPTY } from './format.js';

/**
 * The value a cell sorts by: lower-cased text, or for a numeric column the
 * number behind "$1,234.5", "1.2K" or "12%". An unmeasured cell is null, so it
 * never outranks a real number.
 */
export function sortValue(text, numeric) {
  if (text === '' || text === EMPTY) return null;
  if (!numeric) return text.toLowerCase();

  let s = text.replace(/[$,%\s]/g, '');
  let multiplier = 1;
  if (/K$/i.test(s)) multiplier = 1e3;
  else if (/M$/i.test(s)) multiplier = 1e6;
  else if (/B$/i.test(s)) multiplier = 1e9;
  s = s.replace(/[KMB]$/i, '');

  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? null : n * multiplier;
}

/** Sorts in place and moves the ▲/▼ marker onto the clicked header. */
export function sortTableBy(table, th) {
  const headers = [...th.parentNode.children];
  const index = headers.indexOf(th);
  const tbody = table.tBodies[0];
  if (!tbody) return;

  const dir = th.dataset.dir === 'asc' ? 'desc' : 'asc';

  headers.forEach((h) => {
    h.removeAttribute('data-dir');
    h.querySelector('.sortarrow')?.remove();
  });
  th.dataset.dir = dir;

  const numeric = th.classList.contains('num');

  const valueOf = (td) => sortValue((td?.textContent || '').trim(), numeric);

  [...tbody.rows]
    .sort((a, b) => {
      const x = valueOf(a.cells[index]);
      const y = valueOf(b.cells[index]);
      if (x === null && y === null) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      if (x < y) return dir === 'asc' ? -1 : 1;
      if (x > y) return dir === 'asc' ? 1 : -1;
      return 0;
    })
    .forEach((row) => tbody.appendChild(row));

  th.insertAdjacentHTML('beforeend', ` <span class="sortarrow">${dir === 'asc' ? '▲' : '▼'}</span>`);
}

const FORMULA = /^[=+\-@]/;
const NUMBERISH = /^[-+]?\$?[\d,]*\.?\d+[KMB%]?$/;

/**
 * One CSV cell. Text a spreadsheet would run as a formula (leading = + - @)
 * gets a leading apostrophe, unless it is plainly a number such as -12.5,
 * -$1.20K or +3.10%.
 */
export function csvCell(raw) {
  let text = String(raw ?? '');
  if (FORMULA.test(text) && !NUMBERISH.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function exportCsv(table, filename) {
  const lines = [...table.querySelectorAll('tr')].map((tr) =>
    [...tr.querySelectorAll('th,td')].map((cell) => csvCell((cell.textContent || '').replace(/\s+/g, ' ').trim())).join(',')
  );

  const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'monaco-export.csv';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Roots already wired, so mounting per render cannot stack listeners. */
const wired = new WeakSet();

/**
 * Delegated wiring for `th.sortable` and `[data-csv]`.
 *
 * A CSV button exports the table in its own panel; one outside a panel (the
 * page-head button) exports the first table on the screen, as the prototype did.
 * `data-csv-target="#id"` overrides both.
 */
export function mountTableBehaviour(root = document) {
  if (wired.has(root)) return;
  wired.add(root);

  root.addEventListener('click', (event) => {
    const csv = event.target.closest('[data-csv]');
    if (csv) {
      const scope = csv.closest('.panel') ?? document.getElementById('view') ?? document;
      const table = csv.dataset.csvTarget
        ? document.querySelector(csv.dataset.csvTarget)
        : scope.querySelector('table');

      if (table) exportCsv(table, csv.dataset.csv);
      return;
    }

    const th = event.target.closest('th.sortable');
    if (th) {
      const table = th.closest('table');
      if (table) sortTableBy(table, th);
    }
  });
}
